import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Book, ChevronRight, Building, Wallet, Calendar, 
  Users, AlertTriangle, FileText, Scale, Calculator, HelpCircle,
  ArrowLeft, ExternalLink
} from 'lucide-react';

/**
 * Glossary Page - Comprehensive dictionary of property management terms
 * Helps users understand technical vocabulary used in the application
 */
const Glossaire = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'Tous les termes', icon: Book },
    { id: 'copropriete', label: 'Copropriété', icon: Building },
    { id: 'comptabilite', label: 'Comptabilité', icon: Wallet },
    { id: 'assemblees', label: 'Assemblées', icon: Calendar },
    { id: 'juridique', label: 'Juridique', icon: Scale }
  ];

  const glossaryTerms = [
    // Copropriété
    {
      term: 'Copropriété',
      definition: 'Organisation juridique d\'un immeuble divisé en lots appartenant à plusieurs propriétaires. Chaque coproprietaire possède un lot privatif et une quote-part des parties communes.',
      category: 'copropriete',
      related: ['Lot', 'Parties communes', 'Règlement de copropriété']
    },
    {
      term: 'Lot',
      definition: 'Partie privative d\'un immeuble appartenant à un coproprietaire (appartement, cave, parking...) associée à une quote-part des parties communes exprimée en tantièmes.',
      category: 'copropriete',
      related: ['Tantièmes', 'Parties privatives']
    },
    {
      term: 'Tantièmes',
      definition: 'Quote-part de chaque lot dans les charges communes, définie par le règlement de copropriété. Ils déterminent la répartition des charges entre coproprietaires et le nombre de voix en assemblée générale.',
      category: 'copropriete',
      example: 'Un appartement avec 150 tantièmes sur 10 000 représente 1,5% des charges de la copropriété.',
      related: ['Charges', 'Millièmes']
    },
    {
      term: 'Parties communes',
      definition: 'Éléments de l\'immeuble appartenant à l\'ensemble des coproprietaires : hall d\'entrée, escaliers, toiture, façades, ascenseur, jardins, couloirs...',
      category: 'copropriete',
      related: ['Parties privatives', 'Règlement de copropriété']
    },
    {
      term: 'Parties privatives',
      definition: 'Espaces réservés à l\'usage exclusif d\'un coproprietaire : l\'intérieur de son appartement, les cloisons intérieures, les revêtements de sol...',
      category: 'copropriete',
      related: ['Lot', 'Parties communes']
    },
    {
      term: 'Syndic',
      definition: 'Personne physique ou morale chargée de l\'administration et de la gestion de la copropriété. Il représente le syndicat des coproprietaires, exécute les décisions de l\'AG et gère le budget.',
      category: 'copropriete',
      related: ['Syndicat des coproprietaires', 'Conseil syndical']
    },
    {
      term: 'Syndicat des coproprietaires',
      definition: 'Ensemble des coproprietaires d\'un immeuble, automatiquement constitué dès la création de la copropriété. Il prend les décisions en Assemblée Générale.',
      category: 'copropriete',
      related: ['Copropriété', 'Assemblée Générale']
    },
    {
      term: 'Conseil syndical',
      definition: 'Organe composé de coproprietaires élus qui assiste et contrôle le syndic. Il émet des avis, contrôle la gestion et peut convoquer l\'AG en cas de carence du syndic.',
      category: 'copropriete',
      related: ['Syndic', 'Assemblée Générale']
    },
    {
      term: 'Règlement de copropriété',
      definition: 'Document juridique obligatoire définissant les règles de fonctionnement de la copropriété : répartition des charges, usage des parties communes et privatives, description des lots.',
      category: 'copropriete',
      related: ['État descriptif de division', 'Tantièmes']
    },
    {
      term: 'État descriptif de division',
      definition: 'Document technique annexé au règlement de copropriété qui identifie chaque lot par un numéro et précise sa consistance, sa situation et sa quote-part de parties communes.',
      category: 'copropriete',
      related: ['Règlement de copropriété', 'Lot']
    },
    {
      term: 'Carnet d\'entretien',
      definition: 'Document obligatoire (loi SRU) recensant les informations techniques de l\'immeuble : travaux réalisés, contrats d\'entretien, diagnostics, équipements et leur maintenance.',
      category: 'copropriete',
      related: ['Loi SRU', 'Diagnostics techniques']
    },

    // Comptabilité
    {
      term: 'Exercice comptable',
      definition: 'Période de 12 mois sur laquelle sont calculés les comptes de la copropriété. Il débute généralement au 1er janvier et se termine au 31 décembre, correspondant à la période entre deux AG.',
      category: 'comptabilite',
      related: ['Budget prévisionnel', 'Appels de fonds']
    },
    {
      term: 'Budget prévisionnel',
      definition: 'Estimation des dépenses courantes de la copropriété pour l\'année à venir, voté en Assemblée Générale. Il sert de base au calcul des appels de fonds trimestriels.',
      category: 'comptabilite',
      example: 'Budget prévisionnel de 50 000€ → 4 appels de fonds trimestriels de 12 500€ répartis selon les tantièmes.',
      related: ['Charges courantes', 'Appels de fonds']
    },
    {
      term: 'Appels de fonds',
      definition: 'Sommes demandées aux coproprietaires pour financer les dépenses de la copropriété. Généralement émis trimestriellement (1/4 du budget prévisionnel).',
      category: 'comptabilite',
      related: ['Budget prévisionnel', 'Provisions']
    },
    {
      term: 'Charges courantes',
      definition: 'Dépenses régulières et prévisibles : entretien des parties communes, assurance, honoraires du syndic, électricité, eau, chauffage collectif, gardiennage...',
      category: 'comptabilite',
      related: ['Charges exceptionnelles', 'Budget prévisionnel']
    },
    {
      term: 'Charges exceptionnelles',
      definition: 'Dépenses non prévues au budget : travaux de rénovation, remplacement d\'équipements, mises aux normes... Elles font l\'objet d\'un vote spécifique en AG et d\'appels de fonds dédiés.',
      category: 'comptabilite',
      related: ['Charges courantes', 'Travaux']
    },
    {
      term: 'Impayés',
      definition: 'Charges de copropriété non réglées par un coproprietaire à l\'échéance prévue. Ils peuvent entraîner des pénalités de retard et une procédure de recouvrement.',
      category: 'comptabilite',
      related: ['Relances', 'Procédure de recouvrement']
    },
    {
      term: 'Fonds de travaux',
      definition: 'Provision obligatoire (loi ALUR) constituée par des versements annuels représentant au moins 5% du budget prévisionnel, destinée à financer les futurs travaux importants.',
      category: 'comptabilite',
      related: ['Travaux', 'Loi ALUR']
    },
    {
      term: 'Provisions',
      definition: 'Sommes versées par anticipation par les coproprietaires pour couvrir les dépenses futures de la copropriété. Régularisées en fin d\'exercice selon les dépenses réelles.',
      category: 'comptabilite',
      related: ['Appels de fonds', 'Régularisation']
    },

    // Assemblées
    {
      term: 'Assemblée Générale (AG)',
      definition: 'Réunion annuelle obligatoire des coproprietaires pour voter le budget, approuver les comptes et prendre les décisions importantes concernant l\'immeuble.',
      category: 'assemblees',
      related: ['Ordre du jour', 'Procès-verbal', 'Résolution']
    },
    {
      term: 'Convocation',
      definition: 'Document officiel envoyé aux coproprietaires au moins 21 jours avant l\'AG (article 9 du décret du 17 mars 1967), précisant la date, le lieu et l\'ordre du jour.',
      category: 'assemblees',
      related: ['Assemblée Générale', 'Ordre du jour']
    },
    {
      term: 'Ordre du jour',
      definition: 'Liste des questions soumises au vote lors de l\'Assemblée Générale. Seules les résolutions inscrites à l\'ordre du jour peuvent être votées.',
      category: 'assemblees',
      related: ['Résolution', 'Convocation']
    },
    {
      term: 'Résolution',
      definition: 'Question soumise au vote des coproprietaires lors de l\'AG. Chaque résolution fait l\'objet d\'un vote distinct avec un résultat (adoptée ou rejetée).',
      category: 'assemblees',
      related: ['Majorité', 'Ordre du jour']
    },
    {
      term: 'Quorum',
      definition: 'Nombre minimum de coproprietaires (ou de tantièmes) devant être présents ou représentés pour que l\'AG puisse valablement délibérer. En copropriété, il n\'y a pas de quorum obligatoire pour la première convocation.',
      category: 'assemblees',
      related: ['Assemblée Générale', 'Majorité']
    },
    {
      term: 'Procuration (pouvoir)',
      definition: 'Document par lequel un coproprietaire absent donne pouvoir à une autre personne (mandataire) de voter en son nom lors de l\'AG.',
      category: 'assemblees',
      related: ['Mandataire', 'Vote']
    },
    {
      term: 'Vote par correspondance',
      definition: 'Possibilité pour un coproprietaire de voter sur les résolutions avant l\'AG, sans être présent physiquement ni représenté, en renvoyant un formulaire de vote.',
      category: 'assemblees',
      related: ['Résolution', 'Procuration']
    },
    {
      term: 'Bureau de l\'AG',
      definition: 'Composé du Président de séance (dirige les débats), du Secrétaire (rédige le PV) et du/des Scrutateur(s) (vérifie le décompte des votes). Désigné en début d\'assemblée.',
      category: 'assemblees',
      related: ['Président de séance', 'Procès-verbal']
    },
    {
      term: 'Procès-verbal (PV)',
      definition: 'Document officiel récapitulant les décisions prises en AG : résolutions votées, résultats, discussions. Il doit être signé par les membres du bureau.',
      category: 'assemblees',
      related: ['Bureau de l\'AG', 'Résolution']
    },
    {
      term: 'Feuille de présence',
      definition: 'Document émargé par les coproprietaires présents ou représentés en début d\'AG, permettant de vérifier le quorum et de calculer les majorités.',
      category: 'assemblees',
      related: ['Quorum', 'Tantièmes']
    },

    // Juridique - Majorités
    {
      term: 'Majorité simple (Article 24)',
      definition: 'Majorité des voix exprimées des coproprietaires présents ou représentés. Utilisée pour les décisions courantes : budget, travaux d\'entretien, changement de gardien...',
      category: 'juridique',
      example: 'Si 600 tantièmes sont présents/représentés et votent : Pour = 350, Contre = 200, Abstention = 50 → Résolution adoptée (350 > 200).',
      related: ['Article 25', 'Article 26']
    },
    {
      term: 'Majorité absolue (Article 25)',
      definition: 'Majorité des voix de TOUS les coproprietaires (présents, représentés et absents). Plus stricte, elle s\'applique aux travaux importants, changement de syndic, vidéosurveillance...',
      category: 'juridique',
      example: 'Total copropriété = 10 000 tantièmes → Il faut plus de 5 000 voix POUR adopter la résolution.',
      related: ['Article 24', 'Article 25-1']
    },
    {
      term: 'Passerelle (Article 25-1)',
      definition: 'Mécanisme permettant de voter immédiatement à la majorité de l\'article 24 si la résolution a recueilli au moins 1/3 des voix de tous les coproprietaires.',
      category: 'juridique',
      example: 'Résolution rejetée à l\'art. 25 mais avec 3 500 voix pour (>1/3 de 10 000) → Nouveau vote immédiat à l\'art. 24.',
      related: ['Article 25', 'Article 24']
    },
    {
      term: 'Double majorité (Article 26)',
      definition: 'Majorité des membres du syndicat représentant au moins 2/3 des tantièmes. Nécessaire pour les décisions très importantes : modification du règlement, vente de parties communes...',
      category: 'juridique',
      example: 'Sur 100 coproprietaires (10 000 tantièmes) : il faut au moins 51 coproprietaires représentant 6 667 tantièmes.',
      related: ['Unanimité', 'Article 25']
    },
    {
      term: 'Unanimité',
      definition: 'Accord de TOUS les coproprietaires, sans exception. Requise pour les décisions portant atteinte aux droits de propriété ou à la destination de l\'immeuble.',
      category: 'juridique',
      example: 'Aliénation d\'une partie commune nécessaire à la structure du bâtiment.',
      related: ['Article 26', 'Parties communes']
    },

    // Juridique - Lois
    {
      term: 'Loi du 10 juillet 1965',
      definition: 'Loi fondatrice régissant le statut de la copropriété en France. Elle définit les règles de fonctionnement, les droits et obligations des coproprietaires.',
      category: 'juridique',
      related: ['Décret du 17 mars 1967', 'Copropriété']
    },
    {
      term: 'Décret du 17 mars 1967',
      definition: 'Décret d\'application de la loi de 1965, précisant les règles de convocation des AG, le contenu du règlement de copropriété, les missions du syndic...',
      category: 'juridique',
      related: ['Loi du 10 juillet 1965', 'Convocation']
    },
    {
      term: 'Loi SRU (13 décembre 2000)',
      definition: 'Loi Solidarité et Renouvellement Urbains rendant obligatoire le carnet d\'entretien et le diagnostic technique global pour les copropriétés.',
      category: 'juridique',
      related: ['Carnet d\'entretien', 'Diagnostic technique global']
    },
    {
      term: 'Loi ALUR (24 mars 2014)',
      definition: 'Loi pour l\'Accès au Logement et un Urbanisme Rénové, ayant créé le fonds de travaux obligatoire et renforcé les obligations d\'information des coproprietaires.',
      category: 'juridique',
      related: ['Fonds de travaux', 'Immatriculation']
    },
    {
      term: 'Loi ELAN (23 novembre 2018)',
      definition: 'Loi portant Evolution du Logement, de l\'Aménagement et du Numérique, ayant simplifié certaines règles de copropriété et facilité la prise de décision.',
      category: 'juridique',
      related: ['Vote par correspondance', 'Petites copropriétés']
    }
  ];

  // Filter terms by search and category
  const filteredTerms = glossaryTerms.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.definition.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group terms by first letter
  const groupedTerms = filteredTerms.reduce((acc, term) => {
    const letter = term.term[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(term);
    return acc;
  }, {});

  const sortedLetters = Object.keys(groupedTerms).sort();

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="glossaire-page">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center space-x-2 text-slate-500 hover:text-slate-700 mb-4 transition-colors"
      >
        <ArrowLeft size={18} />
        <span className="text-sm">Retour</span>
      </button>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#00D3A8] to-[#0099CC] rounded-2xl p-6 md:p-8 text-white mb-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <Book size={28} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Glossaire</h1>
            <p className="text-white/80">Lexique des termes de la copropriété</p>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Rechercher un terme..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50"
            data-testid="glossaire-search"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedCategory === cat.id
                  ? 'bg-[#00D3A8] text-white shadow-sm'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-[#00D3A8]'
              }`}
              data-testid={`category-${cat.id}`}
            >
              <Icon size={16} />
              <span>{cat.label}</span>
            </button>
          );
        })}
      </div>

      {/* Terms count */}
      <p className="text-sm text-slate-500 mb-4">
        {filteredTerms.length} terme{filteredTerms.length > 1 ? 's' : ''} trouvé{filteredTerms.length > 1 ? 's' : ''}
      </p>

      {/* Alphabetical Index */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white rounded-lg p-2 border border-slate-100">
        {sortedLetters.map(letter => (
          <a
            key={letter}
            href={`#letter-${letter}`}
            className="w-8 h-8 flex items-center justify-center text-sm font-medium text-slate-600 hover:bg-[#00D3A8] hover:text-white rounded transition-colors"
          >
            {letter}
          </a>
        ))}
      </div>

      {/* Terms List */}
      <div className="space-y-8">
        {sortedLetters.map(letter => (
          <div key={letter} id={`letter-${letter}`}>
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-10 h-10 bg-[#00D3A8] rounded-lg flex items-center justify-center text-white font-bold text-lg">
                {letter}
              </div>
              <div className="h-px flex-1 bg-slate-200"></div>
            </div>

            <div className="space-y-3">
              {groupedTerms[letter].map((item, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border border-slate-100 p-4 md:p-5 hover:shadow-sm transition-all"
                  data-testid={`term-${item.term.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {item.term}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-3">
                    {item.definition}
                  </p>
                  
                  {item.example && (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-3">
                      <p className="text-xs text-blue-700">
                        <strong>Exemple :</strong> {item.example}
                      </p>
                    </div>
                  )}

                  {item.related && item.related.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                      <span className="text-xs text-slate-400">Voir aussi :</span>
                      {item.related.map((rel, i) => (
                        <button
                          key={i}
                          onClick={() => setSearchQuery(rel)}
                          className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded hover:bg-[#00D3A8]/10 hover:text-[#00D3A8] transition-colors"
                        >
                          {rel}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {filteredTerms.length === 0 && (
        <div className="text-center py-12">
          <HelpCircle className="mx-auto text-slate-200 mb-4" size={48} />
          <p className="text-slate-500">Aucun terme trouvé pour "{searchQuery}"</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
            }}
            className="mt-4 text-[#00D3A8] text-sm font-medium hover:underline"
          >
            Réinitialiser les filtres
          </button>
        </div>
      )}

      {/* Legal reference */}
      <div className="mt-8 bg-slate-50 rounded-xl p-6 text-center">
        <p className="text-sm text-slate-600 mb-2">
          Ce glossaire est fourni à titre informatif et ne constitue pas un conseil juridique.
        </p>
        <a
          href="https://www.legifrance.gouv.fr/loda/id/LEGITEXT000006068256"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#00D3A8] text-sm font-medium hover:underline inline-flex items-center space-x-1"
        >
          <span>Consulter la loi du 10 juillet 1965 sur Légifrance</span>
          <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
};

export default Glossaire;
